import { Button, Col, Row, Table, Typography, Select, message, Input } from "antd"
import React, { useEffect, useState } from "react"
import PageHeader from "../../../components/Elements/PageHeader"
import { ContainerOutlined, SearchOutlined } from "@ant-design/icons"
import { db } from "../../../firebase/config"
import { collection, onSnapshot, query, where, doc, writeBatch } from "firebase/firestore"
import { useCollection } from "../../../hooks/useCollection"

const { Text } = Typography
const { Option } = Select

export default function InvoiceClientMatching() {
  // Fetch sales users
  const { data: users, loading: usersLoading } = useCollection("users", [
    "group",
    "array-contains",
    "sales",
  ])

  // Grouped invoice data for the table
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [updating, setUpdating] = useState(false)

  // State for search filter
  const [tempFilter, setTempFilter] = useState("unknown")
  const [activeFilter, setActiveFilter] = useState("unknown")

  // Fetch invoices based on active filter and group them by client.
  useEffect(() => {
    setLoading(true)
    const invoicesRef = collection(db, "invoices")
    let q
    if (activeFilter === "unknown") {
      q = query(invoicesRef, where("user", "==", "unknown"))
    } else {
      // Find the selected user to get the email for querying.
      const selectedUser = users?.find((u) => u.id === activeFilter)
      q = query(invoicesRef, where("user", "==", selectedUser ? selectedUser.email : "unknown"))
    }

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const groups = {}
        querySnapshot.forEach((docSnap) => {
          const invoice = { id: docSnap.id, ...docSnap.data() }
          // Group invoices by the client name; fallback to "Unknown Client" if missing.
          const clientName = invoice?.client?.client || "Unknown Client"
          if (!groups[clientName]) {
            groups[clientName] = {
              id: clientName, // use client name as the group id
              docPrefix: invoice.docPrefix,
              client: invoice.client,
              invoiceCount: 0,
              invoices: [],
              selectedUserId: null, // initially no user is selected
            }
          }
          groups[clientName].invoiceCount += 1
          groups[clientName].invoices.push(invoice)
        })
        setData(Object.values(groups))
        setLoading(false)
      },
      (error) => {
        console.error("Error fetching invoices: ", error)
        setLoading(false)
      }
    )
    return () => unsubscribe()
  }, [activeFilter, users])

  // Handler for selecting a user for a given client group.
  const handleUserSelect = (clientId, value) => {
    setData((prevData) =>
      prevData.map((group) => {
        if (group.id === clientId) {
          return { ...group, selectedUserId: value }
        }
        return group
      })
    )
  }

  // Handler for "Match Selected" button
  const handleMatchSelected = async () => {
    setUpdating(true)
    try {
      const batch = writeBatch(db)
      // Iterate through each group that has a selected user.
      for (const group of data) {
        if (group.selectedUserId) {
          // Look up user details using the selected user ID.
          const selectedUser = users.find((u) => u.id === group.selectedUserId)
          if (!selectedUser) continue

          // For each invoice in the group, update the invoice with the selected user's details.
          group.invoices.forEach((invoice) => {
            const invoiceRef = doc(db, "invoices", invoice.id)
            batch.update(invoiceRef, {
              user: selectedUser.email,
              userId: selectedUser.id,
            })
          })
        }
      }
      await batch.commit()
      message.success("Invoices updated successfully")
    } catch (error) {
      console.error("Error updating invoices: ", error)
      message.error("Failed to update invoices")
    }
    setUpdating(false)
  }

  // Define the table columns.
  const columns = [
    {
      title: "Series",
      dataIndex: "docPrefix",
      width: "10%",
      align: "left",
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Client",
      dataIndex: ["client", "client"],
      align: "left",
      width: "30%",
      sorter: (a, b) => (a.client?.client || "").localeCompare(b.client?.client || ""),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search client"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}>
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.client?.client.toString().toLowerCase().includes(value.toLowerCase()),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Reg nr.",
      dataIndex: ["client", "reg"],
      key: "clientreg",
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search client"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ width: 188, marginBottom: 8, display: "block" }}
          />
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90, marginRight: 8 }}>
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters && clearFilters()
              confirm()
            }}
            size="small"
            style={{ width: 90 }}>
            Reset
          </Button>
        </div>
      ),
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      onFilter: (value, record) =>
        record.client?.reg.toString().toLowerCase().includes(value.toLowerCase()),
      render: (value, record) =>
        record.deleted ? (
          <Text delete style={{ color: "#f5222d" }}>
            {value}
          </Text>
        ) : (
          <Text>{value}</Text>
        ),
    },
    {
      title: "Invoice Count",
      dataIndex: "invoiceCount",
      key: "invoiceCount",
      width: "15%",
    },
    {
      title: "Select User for this",
      dataIndex: "selectedUserId",
      key: "selectedUserId",
      width: "30%",
      render: (value, record) => (
        <Select
          style={{ width: "100%" }}
          placeholder="Select a user"
          value={record.selectedUserId}
          onChange={(val) => handleUserSelect(record.id, val)}
          loading={usersLoading}>
          {users &&
            users.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.email}
              </Option>
            ))}
        </Select>
      ),
    },
  ]

  return (
    <div style={{ padding: 15 }}>
      <Row gutter={[15, 15]}>
        <Col span={24}>
          <PageHeader icon={<ContainerOutlined />} text="Client Match" />
        </Col>
        <Col span={24}>
          <Row gutter={[15, 15]} align="middle">
            <Col>
              <Button type="primary" onClick={handleMatchSelected} loading={updating}>
                Match Selected
              </Button>
            </Col>
            <Col>
              <Select
                style={{ width: 200 }}
                placeholder="Select a user"
                value={tempFilter}
                onChange={(val) => setTempFilter(val)}
                loading={usersLoading}>
                <Option key="unknown" value="unknown">
                  Unknown
                </Option>
                {users &&
                  users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.email}
                    </Option>
                  ))}
              </Select>
            </Col>
            <Col>
              <Button type="primary" onClick={() => setActiveFilter(tempFilter)}>
                Search
              </Button>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Table
            size="small"
            rowKey={(record) => record.id}
            dataSource={data}
            loading={loading}
            columns={columns}
            pagination={false}
          />
        </Col>
      </Row>
    </div>
  )
}
